export type CategoryKey =
  | '25'
  | '17'
  |'22'
  | '30'
  | '31'
  | '32'
  | '33'
  | '35'
  | '36'
  | '37'
  | '38'
  | '100'
  | 'city'
  | 'buy_sell'
  | 'dating'
  | 'events'
  | 'jobs'
  | 'housing'
  | 'cars';

  export type LangKey =
  | 'en' | 'fr' | 'de' | 'es' | 'it' | 'nl' | 'pl' | 'pt' | 'el'
  | 'sv' | 'da' | 'fi' | 'no' | 'cs' | 'hu' | 'ro' | 'bg' | 'hr'
  | 'lt' | 'lv' | 'et' | 'zhCN' | 'ar';

export type TabItem = {
  key: CategoryKey;
  path: string;
  label?: string;
  routineKey?: string;
};

export type ViewMode = 'simple' | 'regular';

export type CityRoutineKey =
  | 'buy_sell'
  | 'dating'
  | 'events'
  | 'jobs'
  | 'house'
  | 'cars';

export const cityRoutineCategories: { key: CityRoutineKey; path: string }[] = [
  { key: 'buy_sell', path: 'city/buy-sell' },
  { key: 'dating', path: 'city/dating' },
  { key: 'events', path: 'city/events' },
  { key: 'jobs', path: 'city/jobs' },
  { key: 'house', path: 'city/house' },
  { key: 'cars', path: 'city/cars' },
];

export const cityRoutineLabels: Record<
  CityRoutineKey,
  { en: string; es: string; ar: string; zhCN: string }
> = {
  buy_sell: {
    en: 'Buy & Sell',
    es: 'Compra y Venta',        // ✅ correct
    ar: 'بيع وشراء',
    zhCN: '买卖',
  },
  dating: {
    en: 'Dating',
    es: 'Citas',                 // ✅ correct (NOT “Citando”)
    ar: 'مواعدة',
    zhCN: '交友',
  },
  events: {
    en: 'Events',
    es: 'Eventos',               // ✅ correct
    ar: 'فعاليات',
    zhCN: '活动',
  },
  jobs: {
    en: 'Jobs',
    es: 'Empleos',               // ✅ better than “Trabajo”
    ar: 'وظائف',
    zhCN: '工作',
  },
  house: {
    en: 'Housing',
    es: 'Bienes Raíces',         // ✅ native / professional
    ar: 'سكن',
    zhCN: '房产',
  },
  cars: {
    en: 'Cars',
    es: 'Autos',                 // ✅ Latin American standard
    ar: 'سيارات',
    zhCN: '汽车',
  },
};



export interface NewsItem {
    id: string;
    title: string;
    description?: string;
    url: string;
    imageUrl?: string;
    publishedAt: number; // Firestore Timestamp (ms since epoch) stored as number for client
    category: CategoryKey;
    city?: string; // for local news filter (e.g., "Tampa")
    country?: string;
    state?: string;
    language: string; // e.g., 'en', 'es'
    source?: string;
    videoFilePath?: string;
     IsHeadline?: boolean;     // ⭐ new flag
}


export interface UserPrefs {
  language: string;
  country?: string;
  state?: string;
  city: string;
  viewMode: ViewMode;  
   userId?: string;   // 🔥 NEW
  userName?: string; // for comments
  email?: string;    // optional
  password?: string; // optional, only for local prefs
  
  tabPrefs?: string[]; // <-- add this
}
export interface NewsComment {
  id: string;          // unique comment ID
  parentId?: string;   // undefined for root comments
  newsId: string;      // which news this belongs to
  user: string;        // commenter name
  description : string;
  timestamp: number;
   city?: string;
  state?: string;
  country?: string;
  language?: string;
  routineKey?: string | null;
  title?: string;         // optional, post title
  imageUrl?: string;      // optional, if attached
  children?: NewsComment[];
}

export // ISO-3166-1 alpha-2
const countryCodeMap: Record<string, string> = {
  "USA": "US",
  "United States": "US",
  "Canada": "CA",
  "United Kingdom": "GB",
  "Germany": "DE",
  "France": "FR",
  "Italy": "IT",
  "Spain": "ES",
  "Netherlands": "NL",
  "Belgium": "BE",
  "Poland": "PL",
  "Sweden": "SE",
  "Austria": "AT",
  "Czech Republic": "CZ",
  "Portugal": "PT",
  "Greece": "GR",
  "Croatia": "HR",
  "Romania": "RO",
  "Bulgaria": "BG",
  "Hungary": "HU",
  "Lithuania": "LT",
  "Latvia": "LV",
  "Finland": "FI",
  "Denmark": "DK",
  "Norway": "NO",
  "Estonia": "EE"
};

export const stateCodeMap: Record<string, string> = {
  // 🇺🇸 USA
  "Alabama": "AL",
  "Arizona": "AZ",
  "California": "CA",
  "Colorado": "CO",
  "District of Columbia": "DC",
  "Florida": "FL",
  "Georgia": "GA",
  "Illinois": "IL",
  "Indiana": "IN",
  "Kansas": "KS",
  "Kentucky": "KY",
  "Massachusetts": "MA",
  "Michigan": "MI",
  "Minnesota": "MN",
  "Missouri": "MO",
  "Nebraska": "NE",
  "Nevada": "NV",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "Ohio": "OH",
  "Oklahoma": "OK",
  "Pennsylvania": "PA",
  "Tennessee": "TN",
  "Texas": "TX",
  "Utah": "UT",
  "Washington": "WA",
  "Wisconsin": "WI",

  // 🇨🇦 Canada
  "Alberta": "AB",
  "British Columbia": "BC",
  "Manitoba": "MB",
  "Ontario": "ON",
  "Quebec": "QC",
  "Saskatchewan": "SK",

  // 🇬🇧 UK
  "England": "ENG",
  "Scotland": "SCT",

  // 🇩🇪 Germany
  "Bavaria": "BY",
  "Berlin": "BE",
  "Bremen": "HB",
  "Hamburg": "HH",
  "Hesse": "HE",
  "Lower Saxony": "NI",
  "North Rhine-Westphalia": "NW",
  "Baden-Württemberg": "BW",
  "Saxony": "SN",

  // 🇫🇷 France
  "Île-de-France": "IDF",
  "Grand Est": "GES",
  "Hauts-de-France": "HDF",
  "Occitanie": "OCC",
  "Nouvelle-Aquitaine": "NAQ",
  "Pays de la Loire": "PDL",
  "Provence-Alpes-Côte d'Azur": "PAC",
  "Auvergne-Rhône-Alpes": "ARA",

  // 🇮🇹 Italy
  "Lombardy": "LOM",
  "Lazio": "LAZ",
  "Veneto": "VEN",
  "Piedmont": "PIE",
  "Emilia-Romagna": "EMR",
  "Liguria": "LIG",
  "Tuscany": "TUS",
  "Apulia": "PUG",
  "Campania": "CAM",
  "Sicily": "SIC",

  // 🇪🇸 Spain
  "Community of Madrid": "MD",
  "Catalonia": "CT",
  "Andalusia": "AN",
  "Valencian Community": "VC",
  "Basque Country": "PV",
  "Aragon": "AR",
  "Canary Islands": "CN",
  "Balearic Islands": "IB",
  "Region of Murcia": "MC",

  // 🇳🇱 Netherlands
  "North Holland": "NH",
  "South Holland": "ZH",
  "Utrecht": "UT",
  "North Brabant": "NB",

  // 🇧🇪 Belgium
  "Flanders": "VLG",
  "Wallonia": "WAL",
  "Brussels-Capital Region": "BRU",

  // 🇵🇱 Poland
  "Masovian": "MZ",
  "Lower Silesian": "DS",
  "Pomeranian": "PM",
  "West Pomeranian": "ZP",
  "Greater Poland Voivodeship": "WP",
  "Lublin Voivodeship": "LU",
  "Łódź Voivodeship": "LD",
  "Kuyavian-Pomeranian Voivodeship": "KP",
  "Lesser Poland Voivodeship": "MA",

  // 🇸🇪 Sweden
  "Stockholm County": "AB",
  "Skåne County": "M",
  "Västra Götaland": "O",

  // 🇨🇿 Czech
  "Prague": "PR",

  // 🇦🇹 Austria
  "Vienna": "W",

  // 🇩🇰 Denmark
  "Capital Region": "H",

  // 🇫🇮 Finland
  "Uusimaa": "18",

  // 🇳🇴 Norway
  "Oslo": "03",

  // 🇭🇺 Hungary
  "Central Hungary": "BU",

  // 🇱🇻 Latvia
  "Riga": "RIX",

  // 🇱🇹 Lithuania
  "Vilnius County": "VL",

  // 🇭🇷 Croatia
  "City of Zagreb": "ZG",

  // 🇷🇴 Romania
  "Bucharest": "B",

  // 🇧🇬 Bulgaria
  "Sofia City Province": "SO"
};

export type CityInput = {
  Country: string;
  State: string;
  City: string;
  lat: number;
  lng: number;
};

export type CityOutput = CityInput & {
  CountryCode: string;
  StateCode: string;
};

export function enrichLocations(data: CityInput[]): CityOutput[] {
  return data.map(item => ({
    ...item,
    CountryCode: countryCodeMap[item.Country] ?? "XX",
    StateCode: stateCodeMap[item.State] ?? "XX"
  }));
}
